import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Calendar, Users, Star, IndianRupee, Stethoscope, Clock, Package, Pill, FileText, PawPrint, HandHeart, ArrowRight, Truck, Syringe, AlertTriangle, Phone, Mail, Wallet } from "lucide-react";
import { Link } from "react-router-dom";

const VET_QUICK_ACTIONS = [
  { to: "/app/vet/visits", icon: Calendar, label: "Today's visits", desc: "Complete & log earnings" },
  { to: "/app/vet/patients", icon: PawPrint, label: "Patients", desc: "Open patient files & history" },
  { to: "/app/vet/availability", icon: Clock, label: "Slot calendar", desc: "Set your bookable hours" },
  { to: "/app/vet/inventory", icon: Package, label: "Inventory", desc: "Track medical supplies" },
  { to: "/app/vet/medicines", icon: Pill, label: "Medicines", desc: "Curate your formulary" },
  { to: "/app/vet/template", icon: FileText, label: "Rx template", desc: "Personalise prescriptions" },
  { to: "/app/vet/pro-bono", icon: HandHeart, label: "Pro-bono", desc: "Help NGO rescue cases" },
  { to: "/app/payout-setup", icon: Wallet, label: "Payouts setup", desc: "Bank · UPI · auto-settle" },
  { to: "/app/van-schedule", icon: Truck, label: "Mobile Vet Van", desc: "Run vaccination drives" },
];

export default function VetDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [vac, setVac] = useState(null);
  useEffect(() => {
    axios.get(`${API}/partner/vet/dashboard`).then(r => setData(r.data)).catch(() => setData("err"));
    axios.get(`${API}/partner/vet/vaccination-alerts`, { params: { window_days: 30 } }).then(r => setVac(r.data)).catch(() => setVac({ alerts: [], counts: { overdue: 0, due_today: 0, upcoming: 0 } }));
  }, []);

  if (data === null) return <div className="text-ink-soft">Loading…</div>;
  if (data === "err") return <NotPartner />;

  const { profile, stats, upcoming, recent } = data;

  return (
    <div className="space-y-6" data-testid="vet-dashboard-page">
      <header className="rounded-3xl bg-gradient-to-br from-sky to-sky-hover text-white p-6 md:p-8 relative overflow-hidden">
        <div className="relative z-10 flex items-center gap-4">
          <img src={profile.photo} className="w-16 h-16 rounded-2xl object-cover border-2 border-white/30" alt=""/>
          <div>
            <div className="text-xs uppercase tracking-wider font-bold opacity-80">Vet Doctor Dashboard</div>
            <h1 className="font-heading text-2xl md:text-3xl font-extrabold">Welcome back, {user?.name?.split(" ")[0]}!</h1>
            <div className="opacity-90 text-sm">{profile.clinic} · {profile.city}</div>
          </div>
        </div>
        <Stethoscope className="absolute right-6 top-6 w-16 h-16 opacity-20"/>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat icon={Calendar} label="Upcoming visits" value={stats.upcoming_appointments} color="bg-sky/15 text-sky"/>
        <Stat icon={Users} label="Patients seen" value={stats.patients_seen} color="bg-leaf/30 text-ink"/>
        <Stat icon={Star} label="Rating" value={`${stats.rating}★`} color="bg-sunshine/30 text-ink"/>
        <Stat icon={IndianRupee} label="Revenue (m)" value={`₹${stats.revenue_this_month}`} color="bg-brand/15 text-brand"/>
      </div>

      <VaccinationAlerts data={vac}/>

      <section data-testid="vet-quick-actions">
        <h2 className="font-heading text-xl font-bold mb-3">Quick actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {VET_QUICK_ACTIONS.map(a => (
            <Link key={a.to} to={a.to} data-testid={`qa-${a.to.split('/').pop()}`} className="bg-surface border border-line rounded-2xl p-4 hover:-translate-y-0.5 hover:shadow-soft transition-all group">
              <div className="w-9 h-9 rounded-xl bg-sky/10 text-sky grid place-items-center mb-2"><a.icon className="w-4 h-4"/></div>
              <div className="font-heading font-extrabold text-sm">{a.label}</div>
              <div className="text-[11px] text-ink-soft mt-0.5">{a.desc}</div>
              <ArrowRight className="w-3 h-3 text-ink-soft group-hover:text-brand mt-1.5"/>
            </Link>
          ))}
        </div>
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="vet-upcoming">
        <h2 className="font-heading text-xl font-bold mb-3">Upcoming appointments</h2>
        {upcoming.length === 0 && <Empty text="No upcoming visits."/>}
        <div className="divide-y divide-line">
          {upcoming.map(a => (
            <div key={a.appointment_id} className="py-3 flex items-center justify-between gap-3">
              <div>
                <div className="font-bold">{a.pet_name} · {a.service}</div>
                <div className="text-xs text-ink-soft">{new Date(a.date_time).toLocaleString()}</div>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-leaf/30">Upcoming</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="vet-recent">
        <h2 className="font-heading text-xl font-bold mb-3">Recent visits</h2>
        {recent.length === 0 && <Empty text="No completed visits yet."/>}
        <div className="divide-y divide-line">
          {recent.map(a => (
            <div key={a.appointment_id} className="py-3 flex items-center justify-between gap-3">
              <div>
                <div className="font-bold">{a.pet_name} · {a.service}</div>
                <div className="text-xs text-ink-soft">{new Date(a.date_time).toLocaleString()}</div>
              </div>
              <span className="text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-surface-muted text-ink-soft">Completed</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-surface border border-line rounded-3xl p-4 shadow-soft">
      <div className={`w-9 h-9 rounded-xl grid place-items-center mb-2 ${color}`}><Icon className="w-4 h-4"/></div>
      <div className="text-xs uppercase tracking-wider font-bold text-ink-soft">{label}</div>
      <div className="font-heading text-2xl font-extrabold mt-0.5">{value}</div>
    </div>
  );
}

const Empty = ({ text }) => <div className="text-ink-soft text-sm py-2">{text}</div>;

function VaccinationAlerts({ data }) {
  if (!data) return null;
  const { alerts = [], counts = { overdue: 0, due_today: 0, upcoming: 0 } } = data;
  const total = (counts.overdue || 0) + (counts.due_today || 0) + (counts.upcoming || 0);
  return (
    <section className="bg-surface border border-line rounded-3xl p-5 shadow-soft" data-testid="vac-alerts-widget">
      <header className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h2 className="font-heading text-xl font-bold inline-flex items-center gap-2">
          <Syringe className="w-5 h-5 text-brand"/> Vaccinations due
          <span className="text-xs font-bold text-ink-soft">· next 30 days</span>
        </h2>
        <div className="flex gap-2 flex-wrap text-[11px] font-bold uppercase tracking-wider" data-testid="vac-alerts-counts">
          <span className="bg-danger/10 text-danger rounded-full px-2.5 py-1">Overdue {counts.overdue}</span>
          <span className="bg-sun/40 text-ink rounded-full px-2.5 py-1">Due today {counts.due_today}</span>
          <span className="bg-leaf/30 text-ink rounded-full px-2.5 py-1">Upcoming {counts.upcoming}</span>
        </div>
      </header>
      {total === 0 ? (
        <div className="text-ink-soft text-sm py-2" data-testid="vac-alerts-empty">
          <AlertTriangle className="w-4 h-4 inline-block mr-1.5 text-leaf"/>
          You're all caught up — no vaccinations due in the next 30 days.
        </div>
      ) : (
        <div className="divide-y divide-line">
          {alerts.slice(0, 8).map(a => <VaccinationRow key={`${a.pet_id}-${a.vaccine_name}`} a={a}/>)}
          {alerts.length > 8 && (
            <div className="pt-3 text-xs text-ink-soft font-bold text-center">
              + {alerts.length - 8} more — open patient list to see all
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function VaccinationRow({ a }) {
  const tone =
    a.status === "overdue"   ? { bg: "bg-danger/10",  text: "text-danger",   label: `Overdue · ${Math.abs(a.days_remaining)}d ago` }
    : a.status === "due_today" ? { bg: "bg-sun/40",   text: "text-ink",      label: "Due today" }
    : a.days_remaining <= 7   ? { bg: "bg-brand/10",  text: "text-brand",    label: `In ${a.days_remaining}d` }
                              : { bg: "bg-leaf/30",   text: "text-ink",      label: `In ${a.days_remaining}d` };
  return (
    <div className="py-3 flex items-center gap-3" data-testid={`vac-alert-${a.pet_id}`}>
      {a.pet_photo && a.pet_photo.trim() ? (
        <img src={a.pet_photo} alt={a.pet_name} className="w-10 h-10 rounded-xl object-cover shrink-0"/>
      ) : (
        <div className="w-10 h-10 rounded-xl bg-surface-muted grid place-items-center text-ink-soft shrink-0"><PawPrint className="w-4 h-4"/></div>
      )}
      <div className="flex-1 min-w-0">
        <div className="font-bold truncate">{a.pet_name} <span className="text-ink-soft font-normal text-xs">· {a.breed || a.species}</span></div>
        <div className="text-xs text-ink-soft truncate">
          <Syringe className="w-3 h-3 inline-block mr-1"/>{a.vaccine_name} · owner {a.owner_name || "—"}
        </div>
      </div>
      <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 ${tone.bg} ${tone.text}`}>{tone.label}</span>
      <div className="flex items-center gap-1.5">
        {a.owner_phone && (
          <a href={`tel:${a.owner_phone}`} title={`Call ${a.owner_name || "owner"}`} data-testid={`vac-call-${a.pet_id}`} className="w-8 h-8 rounded-full grid place-items-center bg-surface-muted hover:bg-brand hover:text-white transition-colors">
            <Phone className="w-3.5 h-3.5"/>
          </a>
        )}
        {a.owner_email && (
          <a href={`mailto:${a.owner_email}?subject=${encodeURIComponent(`Reminder: ${a.vaccine_name} due for ${a.pet_name}`)}&body=${encodeURIComponent(`Hi ${a.owner_name || ""},\n\nThis is a quick reminder that ${a.pet_name}'s ${a.vaccine_name} vaccination is due on ${a.next_due}. Please reply to book a slot.\n\n— Your vet at PetBond`)}`} title={`Email ${a.owner_name || "owner"}`} data-testid={`vac-email-${a.pet_id}`} className="w-8 h-8 rounded-full grid place-items-center bg-surface-muted hover:bg-brand hover:text-white transition-colors">
            <Mail className="w-3.5 h-3.5"/>
          </a>
        )}
        <Link to={`/app/vet/prescribe/${a.pet_id}`} title="Open patient" data-testid={`vac-open-${a.pet_id}`} className="bg-brand text-white rounded-full px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-brand-hover">
          Open <ArrowRight className="w-3 h-3"/>
        </Link>
      </div>
    </div>
  );
}

export function NotPartner() {
  return (
    <div className="bg-surface border border-line rounded-3xl p-8 text-center" data-testid="not-partner">
      <div className="font-heading text-xl font-bold">This dashboard is only for partners.</div>
      <p className="text-ink-soft mt-1">Visit <a href="/app/partner" className="text-brand font-bold">/app/partner</a> to sign up as a vet, store, or hostel.</p>
    </div>
  );
}
